import { postAddHelpful, postRemoveHelpful } from '@/api/product';
import { IReviewsData } from '@/api/types/product';
import Image from '@/components/Image';
import ImagePreview, { ImagePreviewRef } from '@/components/ImagePreview';
import StarRating from '@/components/StarRating';
import { useStore } from '@/providers/app-store-provider';
import { colors, font } from '@/theme/zeelool.theme';
import { env } from '@/utils/env';
import { formatNumber, getDisplayStars, getIntlLocale, nameOmission } from '@/utils/tools';
import { Box, LinearProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslations } from '@/i18n/use-translations';
import { useDebounceFn } from 'ahooks';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// 自定义样式组件
const PCReviewItem = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  borderBottom: '1px solid #eee',
  padding: '25px 0',
});

const PCLeftBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexDirection: 'column',
});

const PCRightBox = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  height: 'auto',
  minHeight: '8.14vw',
  marginLeft: '168px',
  paddingRight: '16px',
  gap: '36px',
  width: '100%',
});

const PCStarBox = styled(Box)({
  marginBottom: '12px',
  '& .nick-name': {
    display: 'block',
    fontFamily: font.bb,
    fontSize: '16px',
    color: colors.primaryBlack,
    lineHeight: '19px',
    textAlign: 'left',
    fontStyle: 'normal',
    textTransform: 'none',
  },
  '& .star': {
    marginBottom: '8px',
    '& .MuiRating-icon': {
      fontSize: '18px',
    },
  },
});

const PCColorBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'waiMode',
})(({ waiMode }: { waiMode: boolean }) => ({
  fontFamily: font.br,
  fontWeight: 'normal',
  fontSize: '14px',
  color: waiMode ? colors.primaryBlack : colors.auxiliaryText,
  lineHeight: '17px',
  textAlign: 'left',
  fontStyle: 'normal',
  textTransform: 'none',
  marginBottom: '16px',
}));

const PCReviewText = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: '32px',
  '& .review-title': {
    fontFamily: font.bb,
    fontWeight: 'normal',
    fontSize: '20px',
    color: colors.primaryBlack,
    lineHeight: '20px',
    textAlign: 'left',
    fontStyle: 'normal',
    textTransform: 'none',
    wordBreak: 'break-word',
  },
  '& .review-content': {
    fontFamily: font.ir,
    fontWeight: 400,
    fontSize: '14px',
    color: colors.primaryBlack,
    lineHeight: '22px',
    textAlign: 'left',
    fontStyle: 'normal',
    textTransform: 'none',
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
  },
});

const PCImgList = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  position: 'relative',
  gap: '10px',
  '& .img-item': {
    width: '120px',
    height: '120px',
  },
  '& .other-num': {
    backgroundColor: colors.primaryBlack,
    opacity: 0.5,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    right: 0,
    color: '#fff',
    fontSize: '16px',
    cursor: 'pointer',
    pointerEvents: 'none',
  },
});

const PCActionBtn = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'waiMode',
})(({ waiMode }: { waiMode: boolean }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  '& .iconfont': {
    cursor: 'pointer',
    width: '16px',
  },
  '& .helpful': {
    fontFamily: font.br,
    fontWeight: 'normal',
    fontSize: '14px',
    color: colors.primaryBlack,
    lineHeight: '17px',
    textAlign: 'left',
    fontStyle: 'normal',
    textTransform: 'none',
    paddingLeft: '10px',
    cursor: 'pointer',
    width: '100px',
  },
  '& .facebook': {
    width: '16px',
    height: '16px',
    marginLeft: '26px',
  },
  '& .review-date': {
    fontFamily: font.ir,
    fontWeight: 'normal',
    fontSize: '14px',
    color: waiMode ? colors.primaryBlack : colors.auxiliaryText,
    lineHeight: '17px',
    textAlign: 'left',
    fontStyle: 'normal',
    textTransform: 'none',
    marginLeft: '8px',
  },
}));

const PCReply = styled(Box)({
  width: '100%',
  padding: '16px',
  fontFamily: font.ir,
  fontSize: '14px',
  color: colors.primaryBlack,
  lineHeight: '22px',
  textAlign: 'left',
  fontStyle: 'normal',
  textTransform: 'none',
  background: '#F4F4F4',
});

// 创建扩展的评论项类型，兼容两种数据结构
export type ReviewItemData = (
  | NonNullable<IReviewsData['list']>[number]
  | NonNullable<IReviewsData['get_highest_score']>[number]
) & {
  // 添加可能缺失的字段，使其可选
  title?: string;
  star_detail?: {
    quality?: number;
    fit?: number;
    design?: number;
  };
  review_status?: number;
  color?: string;
  prescription_type?: string;
  reply?: string;
};

export interface IProductDetailsPageInteractionBtnClickParams {
  page_name?: string;
  interaction_type?: string;
  interaction_name?: string;
  comment_category?: string;
  tab_name?: string;
}

interface CustomerReviewsItemProps {
  itemData: ReviewItemData;
  isGlass?: boolean;
  inList?: boolean;
  onViewMore?: () => void;
  className?: string;
  isWeb?: boolean;
  SDProductDetailsPageInteractionBtnClick?: (
    params: IProductDetailsPageInteractionBtnClickParams,
  ) => void;
  tabName?: 'all' | 'image';
}

const CustomerReviewsItem: React.FC<CustomerReviewsItemProps> = ({
  itemData,
  isGlass = false,
  inList = false,
  onViewMore,
  className,
  isWeb = false,
  SDProductDetailsPageInteractionBtnClick,
  tabName,
}) => {
  const t = useTranslations('goodsDetail');
  // 图片预览相关
  const imagePreviewRef = useRef<ImagePreviewRef>(null);
  const token = useStore((state) => state.token);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const waiMode = useStore((state) => state.waiMode);
  const locale = getIntlLocale(env.NEXT_PUBLIC_SITE_NAME);

  const [clickedLogin, setClickedLogin] = useState<boolean>(false);
  const [isHelpful, setIsHelpful] = useState<boolean>(itemData.is_helpful === 1);
  const [helpfulNumber, setHelpfulNumber] = useState<number>(
    (itemData.helpful_number_base || 0) + (itemData.helpful_num || 0),
  );

  useEffect(() => {
    setIsHelpful(itemData.is_helpful === 1);
    setHelpfulNumber((itemData.helpful_number_base || 0) + (itemData.helpful_num || 0));
  }, [itemData]);

  // 处理点赞
  const { run: handleHelpfulClick } = useDebounceFn(
    async () => {
      SDProductDetailsPageInteractionBtnClick?.({
        page_name: '商品详情评论列表',
        interaction_type: '点赞',
        interaction_name: isHelpful ? '取消点赞' : '点赞',
        tab_name: tabName === 'all' ? 'All' : 'Images',
      });
      if (token) {
        if (isHelpful) {
          await postRemoveHelpful({ review_id: itemData.id });
          setIsHelpful(false);
          setHelpfulNumber((prev) => prev - 1);
        } else {
          await postAddHelpful({ review_id: itemData.id });
          setIsHelpful(true);
          setHelpfulNumber((prev) => prev + 1);
        }
      } else {
        setClickedLogin(true);
        setShowLoginRegisterDialog({
          show: true,
          situation: '商品评价',
        });
      }
    },
    { wait: 500, leading: true, trailing: false },
  );

  // 处理点击登录后，登录成功，自动点赞
  useEffect(() => {
    if (clickedLogin && token) {
      handleHelpfulClick();
    }
  }, [clickedLogin, token, handleHelpfulClick]);
  // 计算显示的图片
  const displayImages = useMemo(
    () => (itemData.image_urls || itemData.images || []).slice(0, isWeb ? 6 : 4),
    [itemData.image_urls, itemData.images, isWeb],
  );

  // 所有图片（用于预览）
  const allImages = useMemo(
    // images 兜底已经去掉, 不存在 image_urls 没值, 但images有值的情况
    () => itemData.image_urls || [],
    [itemData.image_urls, itemData.images],
  );

  // 格式化点赞数
  const formatHelpfulNumber = useCallback((num: number) => {
    if (num < 1000) return num;
    if (num >= 1000) {
      const value = (num / 1000).toFixed(1);
      return value.endsWith('.0') ? `${Math.floor(num / 1000)}K` : `${value}K`;
    }
    return num;
  }, []);

  // 安全地获取字段值
  const nickname = useMemo(
    () => itemData.nickname || t('customerReivewsList.Anonymous'),
    [itemData.nickname, t],
  );
  const createdAt = useMemo(() => itemData.created_at || '', [itemData.created_at]);
  const star = useMemo(() => itemData.star || 0, [itemData.star]);

  // 处理图片点击
  const handleImageClick = useCallback((index: number) => {
    imagePreviewRef.current?.handleVisibleChange(index);
  }, []);

  // 获取百分比
  const getPercentage = useCallback((number: number) => {
    try {
      const percent = (number / 5) * 100;
      return isNaN(percent) ? 0 : Math.min(percent, 100);
    } catch {
      return 0;
    }
  }, []);

  const reviewImageAlt = useMemo(() => {
    try {
      return itemData.detail ? `${itemData.detail.slice(0, 70)}` : t('mainPicture.alt.product');
    } catch {
      return t('mainPicture.alt.product');
    }
  }, [itemData.detail]);

  if (isWeb) {
    // PC端布局
    return (
      <PCReviewItem className={className}>
        <PCLeftBox>
          <PCStarBox>
            <StarRating
              value={getDisplayStars(star.toString())}
              readOnly
              precision={0.5}
              size="small"
              className="star"
              iconSize="14px"
              gap="6px"
            />
            <span className="nick-name">{nameOmission(nickname)}</span>
          </PCStarBox>

          <PCColorBox waiMode={waiMode}>
            {itemData.color && (
              <p>
                {t('customerReivewsList.color')}: {itemData.color}
              </p>
            )}
            {isGlass && itemData.prescription_type && (
              <p>
                {t('customerReivewsList.rx')}: {itemData.prescription_type}
              </p>
            )}
          </PCColorBox>

          {/* 详细评分进度条 */}
          {itemData.star_detail && (
            <Box className="flex flex-col justify-between">
              {itemData.star_detail.quality !== undefined &&
                itemData.star_detail.quality !== null && (
                  <Box className="flex items-center justify-between w-[257px] min-w-[257px] mb-[10px]">
                    <span className="text-[14px] w-[80px] leading-[17px]">
                      {t('customerReivewsList.quality')}
                    </span>
                    <LinearProgress
                      variant="determinate"
                      value={getPercentage(Number(itemData.star_detail.quality))}
                      className="w-[165px] mx-[5px]"
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#E0E0E0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: colors.primaryGreen,
                          borderRadius: 3,
                        },
                      }}
                    />
                    <span className="text-[14px] font-bb">
                      {formatNumber(itemData.star_detail.quality, 1, locale)}
                    </span>
                  </Box>
                )}

              {itemData.star_detail.fit !== undefined && itemData.star_detail.fit !== null && (
                <Box className="flex items-center justify-between w-[257px] min-w-[257px] mb-[10px]">
                  <span className="text-[14px] w-[80px] leading-[17px]">
                    {t('customerReivewsList.fit')}
                  </span>
                  <LinearProgress
                    variant="determinate"
                    value={getPercentage(Number(itemData.star_detail.fit))}
                    className="w-[165px] mx-[5px]"
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: '#E0E0E0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: colors.primaryGreen,
                        borderRadius: 3,
                      },
                    }}
                  />
                  <span className="text-[14px] font-bb">
                    {formatNumber(itemData.star_detail.fit, 1, locale)}
                  </span>
                </Box>
              )}

              {itemData.star_detail.design !== undefined &&
                itemData.star_detail.design !== null && (
                  <Box className="flex items-center justify-between w-[257px] min-w-[257px]">
                    <span className="text-[14px] w-[80px] leading-[17px]">
                      {t('customerReivewsList.design')}
                    </span>
                    <LinearProgress
                      variant="determinate"
                      value={getPercentage(Number(itemData.star_detail.design))}
                      className="w-[165px] mx-[5px]"
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#E0E0E0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: colors.primaryGreen,
                          borderRadius: 3,
                        },
                      }}
                    />
                    <span className="text-[14px] font-bb">
                      {formatNumber(itemData.star_detail.design, 1, locale)}
                    </span>
                  </Box>
                )}
            </Box>
          )}
        </PCLeftBox>

        <PCRightBox>
          {(itemData.title || itemData.detail) && (
            <PCReviewText>
              {itemData.title ? (
                <>
                  <div className="review-title break-words">{itemData.title}</div>
                  {itemData.detail && (
                    <p className="review-content break-words">{itemData.detail}</p>
                  )}
                </>
              ) : (
                <div className="review-content break-words" style={{ marginTop: 0 }}>
                  {itemData.detail}
                </div>
              )}
            </PCReviewText>
          )}

          {/* 评论图片 */}
          {allImages.length > 0 && (
            <PCImgList>
              {displayImages.map((imageUrl, index) => (
                <Image
                  key={imageUrl}
                  src={imageUrl}
                  alt={`${reviewImageAlt}${index}`}
                  width={120}
                  height={120}
                  className="img-item object-cover cursor-pointer"
                  onClick={() => handleImageClick(index)}
                  errorComponent={
                    <div className="w-[120px] h-[120px] flex items-center justify-center text-[14px] text-[#c0c4cc] bg-[#f5f7fa]">
                      {t('customerReivewsList.failed')}
                    </div>
                  }
                />
              ))}
              {allImages.length > 6 && (
                <div className="img-item other-num">+ {allImages.length - 6}</div>
              )}
            </PCImgList>
          )}

          {/* 回复内容 */}
          {itemData.reply && itemData.review_status !== 2 && (
            <PCReply>
              {t('customerReivewsList.zeeloolReply')}：<span>{itemData.reply}</span>
            </PCReply>
          )}

          {/* 底部操作按钮 */}
          <PCActionBtn className="notranslate" waiMode={waiMode}>
            <Image
              src={isHelpful ? '/goods-detail/helped.png' : '/goods-detail/help.png'}
              alt="helpful"
              className="iconfont"
              width={16}
              height={16}
              onClick={handleHelpfulClick}
            />
            <span className="helpful" onClick={handleHelpfulClick}>
              {t('customerReivewsList.helpful')}({formatHelpfulNumber(helpfulNumber)})
            </span>

            {itemData.source_label === 'FB' && (
              <Image
                src="/goods-detail/facebook.png"
                alt="facebook"
                className="facebook"
                width={16}
                height={16}
              />
            )}
            {itemData.source_label === 'ins' && (
              <Image
                src="/goods-detail/ins.png"
                alt="instagram"
                className="facebook"
                width={16}
                height={16}
              />
            )}
            <span className="review-date">{createdAt}</span>
          </PCActionBtn>
        </PCRightBox>

        {/* 隐藏的 ImagePreview 组件 */}
        <ImagePreview
          ref={imagePreviewRef}
          images={allImages}
          showThumbnail={false}
          maskOpacity={0.5}
          sx={{ display: 'none' }}
        />
      </PCReviewItem>
    );
  }

  // 移动端布局（保持原有样式）
  return (
    <Box
      className={`bg-white rounded-[10px] p-[12px] ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onViewMore?.();
      }}
    >
      {/* 用户信息和评分 */}
      <Box>
        <Box className="flex items-center justify-between mb-[3px]">
          <Typography variant="body2" className="text-[12px]">
            {nameOmission(nickname)}
          </Typography>

          <Box className="flex items-center">
            {itemData.source_label === 'ins' && (
              <Image
                src="/goods-detail/ins.png"
                alt="Instagram"
                className="w-[12px] h-[12px] mr-[4px]"
                width={12}
                height={12}
              />
            )}
            {itemData.source_label === 'FB' && (
              <Image
                src="/goods-detail/facebook.png"
                alt="Facebook"
                className="w-[12px] h-[12px] mr-[4px]"
                width={12}
                height={12}
              />
            )}
            <Typography
              variant="caption"
              className={`text-[12px] ${waiMode ? 'text-primary-black' : 'text-[#808080]'}`}
            >
              {createdAt}
            </Typography>
          </Box>
        </Box>

        <StarRating
          value={getDisplayStars(star.toString())}
          precision={0.5}
          size="small"
          iconSize="12px"
          gap="4px"
        />
      </Box>

      {/* 评论标题 */}
      {itemData.title && (
        <div className="font-bb text-[16px] pb-6 break-words">{itemData.title}</div>
      )}

      {/* 详细评分 */}
      {itemData.star_detail && inList && (
        <Box className="flex gap-[8px] py-6">
          {itemData.star_detail.quality !== undefined && (
            <div className="bg-[rgba(199,255,87,0.4)] text-[12px] px-12 h-24 flex items-center rounded-[14px]">
              {t('customerReivewsList.quality')}
              <span className="font-bb">
                &nbsp;{formatNumber(itemData.star_detail.quality, 1, locale)}
              </span>
            </div>
          )}
          {itemData.star_detail.fit !== undefined && (
            <div className="bg-[rgba(199,255,87,0.4)] text-[12px] px-12 h-24 flex items-center rounded-[14px]">
              {t('customerReivewsList.fit')}
              <span className="font-bb">
                &nbsp;{formatNumber(itemData.star_detail.fit, 1, locale)}
              </span>
            </div>
          )}
          {itemData.star_detail.design !== undefined && (
            <div className="bg-[rgba(199,255,87,0.4)] text-[12px] px-12 h-24 flex items-center rounded-[14px]">
              {t('customerReivewsList.design')}
              <span className="font-bb">
                &nbsp;{formatNumber(itemData.star_detail.design, 1, locale)}
              </span>
            </div>
          )}
        </Box>
      )}

      {/* 评论内容 */}
      {itemData.detail && (
        <p className="pt-8 pb-6 whitespace-pre-wrap font-ir text-[13px] leading-[20px] break-words">
          {itemData.detail}
        </p>
      )}

      {/* 评论图片 - 使用 ImagePreview 组件 */}
      {allImages.length > 0 && (
        <Box className="py-6">
          <Box className="flex gap-[8px]">
            {displayImages.map((imageUrl, index) => (
              <Box
                key={imageUrl}
                className="relative w-[64px] h-[64px] rounded cursor-pointer overflow-hidden"
                onClick={(e) => {
                  e.stopPropagation();
                  handleImageClick(index);
                }}
              >
                <Image
                  src={imageUrl}
                  alt={`${reviewImageAlt}${index}`}
                  width={64}
                  height={64}
                  errorComponent={
                    <div className="w-[64px] h-[64px] flex items-center justify-center text-[12px] text-[#c0c4cc] bg-[#f5f7fa]">
                      {t('customerReivewsList.failed')}
                    </div>
                  }
                  className="object-cover w-full h-full"
                />
                {allImages.length > 4 && index === 3 && (
                  <div
                    className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white text-[14px] rounded-4"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewMore?.();
                    }}
                  >
                    +{allImages.length - 4}
                  </div>
                )}
              </Box>
            ))}
          </Box>

          {/* 隐藏的 ImagePreview 组件 */}
          <ImagePreview
            ref={imagePreviewRef}
            images={allImages}
            showThumbnail={false}
            maskOpacity={0.5}
            sx={{ display: 'none' }}
          />
        </Box>
      )}

      {/* 回复内容 */}
      {(itemData.review_status === 2 || itemData.reply) && (
        <Box className="bg-gray-100 p-[12px] rounded py-6">
          <p
            className={`break-words text-12 leading-14 ${waiMode ? 'text-primary-black' : 'text-[#666666]'}`}
          >
            {itemData.review_status === 2
              ? t('customerReivewsList.violatingRegulationsContent')
              : `${t('customerReivewsList.zeeloolReply')}: ${itemData.reply}`}
          </p>
        </Box>
      )}

      {/* 底部信息和点赞 */}
      <div className="flex items-center justify-between pt-6">
        <div className="flex items-center gap-[20px]">
          {itemData.color && itemData.order_id && (
            <span className={`text-[12px] ${waiMode ? 'text-primary-black' : 'text-[#808080]'}`}>
              {t('customerReivewsList.color')}: {itemData.color}
            </span>
          )}
          {isGlass && itemData.prescription_type && (
            <span className={`text-[12px] ${waiMode ? 'text-primary-black' : 'text-[#808080]'}`}>
              {t('customerReivewsList.rx')}: {itemData.prescription_type}
            </span>
          )}
        </div>

        {inList && (
          <div className="flex items-center cursor-pointer" onClick={handleHelpfulClick}>
            <Image
              src={isHelpful ? '/goods-detail/helped.png' : '/goods-detail/help.png'}
              alt="helpful"
              className="w-[16px] h-[16px] mr-[4px]"
              width={16}
              height={16}
            />
            <span className={`text-[12px] ${waiMode ? 'text-primary-black' : 'text-[#808080]'}`}>
              {t('customerReivewsList.helpful')} ({formatHelpfulNumber(helpfulNumber)})
            </span>
          </div>
        )}
      </div>
    </Box>
  );
};

export default CustomerReviewsItem;
